import { Typography } from '@material-ui/core';
import { Check, Email, WarningRounded } from '@material-ui/icons';
import { Color, Inline, Stack, useUID } from '@superdispatch/ui';
import {
  Box,
  DescriptionItem,
  MultilineText,
  TextBox,
} from '@superdispatch/ui-lab';
import { useEffect, useMemo, useRef } from 'react';
import {
  formatShipperAddress,
  ShipperProfileDTO,
} from 'shared/modules/shipper/ShipperProfileDTO';
import { WebViewLinkedText } from 'shared/routing/WebViewLinks';
import { CargoInsuranceMinimumDetails } from '../shipper-requirements/CargoInsuranceMinimumDetails';
import { CargoInsuranceMinimumTooltip } from '../shipper-requirements/CargoInsuranceMinimumTooltip';
import { ShipperProfileCard } from './ShipperProfileCard';

interface ShipperProfileRequirementsProps {
  shipperProfile: ShipperProfileDTO;
  onRequirementLinkClick: () => void;
}

export function ShipperProfileRequirements({
  shipperProfile,
  onRequirementLinkClick,
}: ShipperProfileRequirementsProps) {
  const uid = useUID();
  const instructionsUID = `${uid}-instructions`;
  const shipperProfileAddress = useMemo(
    () => formatShipperAddress(shipperProfile),
    [shipperProfile],
  );

  const titleRef = useRef<HTMLDivElement>(null);
  const shouldShowIcon = useMemo(() => {
    const params = new URLSearchParams(window.location.search);

    return (
      params.get('utm_medium') === 'SLB Shipper Requirements' ||
      params.get('utm_source') === 'SLB Shipper Requirements' // support backwards compatibility
    );
  }, []);

  useEffect(() => {
    if (!shouldShowIcon) return;

    const frame = requestAnimationFrame(() => {
      titleRef.current?.scrollIntoView();
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [shouldShowIcon]);

  return (
    <ShipperProfileCard
      title={
        <Inline ref={titleRef} noWrap={true} verticalAlign="center">
          {shouldShowIcon && <WarningRounded htmlColor={Color.Yellow300} />}

          <span>Shipper Requirements</span>
        </Inline>
      }
    >
      <Stack space="small">
        {!!(
          shipperProfile.carrier_insurance_requirements?.length ||
          shipperProfile.carrier_certificate_of_insurance
        ) && (
          <Stack>
            <Typography variant="h4" color="textSecondary">
              Insurance
            </Typography>
            <Stack space="small">
              {!!shipperProfile.carrier_certificate_of_insurance && (
                <Box maxWidth="300px">
                  <DescriptionItem
                    wrap={true}
                    icon={<Check />}
                    label={
                      <TextBox>
                        {shipperProfile.carrier_certificate_of_insurance ===
                        'CERTIFICATE_HOLDER'
                          ? 'List as Certificate Holder'
                          : 'List as Additionally Insured'}
                      </TextBox>
                    }
                  >
                    <Stack space="xsmall">
                      {!!shipperProfileAddress && (
                        <Stack space="xxsmall">
                          <TextBox color="secondary">
                            {shipperProfile.name}
                          </TextBox>
                          <TextBox color="secondary">
                            {shipperProfileAddress}
                          </TextBox>
                        </Stack>
                      )}

                      {!!shipperProfile.contact_email && (
                        <Stack space="xxsmall">
                          <TextBox color="secondary">Send info to:</TextBox>
                          <DescriptionItem
                            icon={<Email />}
                            aria-label="email for the insurance info"
                          >
                            <TextBox color="secondary">
                              {shipperProfile.contact_email}
                            </TextBox>
                          </DescriptionItem>
                        </Stack>
                      )}
                    </Stack>
                  </DescriptionItem>
                </Box>
              )}
              {shipperProfile.carrier_insurance_requirements?.length ? (
                <Stack>
                  <DescriptionItem
                    icon={<Check />}
                    label={
                      <CargoInsuranceMinimumTooltip
                        limits={shipperProfile.carrier_insurance_requirements}
                      />
                    }
                  />
                  <DescriptionItem inset={true}>
                    <CargoInsuranceMinimumDetails
                      limits={shipperProfile.carrier_insurance_requirements}
                    />
                  </DescriptionItem>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        )}

        <Stack>
          <Typography variant="h4" color="textSecondary">
            Other
          </Typography>

          <Stack space="small">
            {shipperProfile.ach_payments && (
              <DescriptionItem
                icon={<Check />}
                label={<TextBox>ACH Payment Information</TextBox>}
              />
            )}
            <Stack>
              <TextBox
                id={instructionsUID}
                color="secondary"
                variant="heading-6"
              >
                Additional Instructions
              </TextBox>

              <MultilineText
                overflowWrap="break-word"
                aria-labelledby={instructionsUID}
              >
                {!shipperProfile.carrier_requirements ? (
                  <TextBox color="secondary">No information provided</TextBox>
                ) : (
                  <WebViewLinkedText onClick={onRequirementLinkClick}>
                    {shipperProfile.carrier_requirements}
                  </WebViewLinkedText>
                )}
              </MultilineText>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </ShipperProfileCard>
  );
}
